import { useCallback } from 'react';

import { useNode } from '@/components/diagrams/FlowChart/hooks/useNode';
import {
  TileEditModal,
  TileEditModalFormShape,
  TileEditModalProps,
} from '@/components/diagrams/FlowChart/nodes/TileNode/TileEditModal';
import { TileNode } from '@/components/diagrams/FlowChart/types';

import { useEntityDiagramContext } from '../contexts/entityDiagram.context';

/**
 * Shows the shared `TileEditModal` and saves through the entity diagram
 * reducer.
 *
 * NOTE: The tile settings modals of the diagram editors are different:
 * - This modal edits "Display as" and the notes (with "Generate"). It does
 *   not edit the tile text, because the text comes from household data.
 * - The estate waterfall uses the same shared modal, with an extra "Show
 *   itemized tax values" setting.
 * - The presentation diagram uses a copy of the form that also edits the name
 *   and the type (`PresentationDiagramTileEditModal`).
 * - The artifact diagram builder edits only the three text lines
 *   (`TileTextEditModal`).
 * Moving all editors to one modal needs design input: https://linear.app/luminary/issue/T1-7153
 */
export function EntityDiagramTileEditModal(props: TileEditModalProps) {
  const { dispatch } = useEntityDiagramContext();
  const node = useNode<TileNode>();

  const onSaveData = useCallback(
    (formData: TileEditModalFormShape) => {
      dispatch({
        type: 'UPDATE_TILE_CONFIG',
        nodeId: node.id,
        notes: formData.notes,
        componentType: formData.componentType,
        iconShape: formData.iconShape,
      });
    },
    [dispatch, node.id]
  );

  return <TileEditModal {...props} onSaveData={onSaveData} />;
}
